import React from "react";

import cx from "classnames";

import { Divider } from "../Divider";
import { IconDictionary } from "../Icon";
import { ProfilePic } from "../ProfilePic";
import { Text } from "../Text";

import styles from "./EmployeeList.scss";

export interface Employee {
  id?: string;
  image30?: string;
  image50?: string;
  image70?: string;
  initials?: string;
  title?: string;
  fullName?: string;
  name?: string;
  jobTitle?: string;
  profileUrl?: string;
  featuredGroup?: string | React.ReactElement;
}

interface EmployeeListProps {
  /** Array of user objects */
  employees: Employee[];
  /** Should list display employee titles */
  showTitles?: boolean;
  /** Should list display secondary text */
  showSecondaryText?: boolean;
  /** Should display a divider at the end of each row */
  showDivider?: boolean;
  /** Does the list turn into a scrollable at 248px? */
  heightLimited?: boolean;
  /** Opens user profile in a new tab */
  openProfileInNewTab?: boolean;
  /** Disables the profile link functionality */
  disableProfileLink?: boolean;
  /** If provided all user icons will render as this icon instead of the profile pictures or initials */
  profilePicIconOverride?: IconDictionary;
  /** Component to be rendered at the end of the employee list */
  footerComponent?: React.ReactNode;
}

interface EmployeeInfoWrapperProps {
  children: React.ReactNode;
  employee: Employee;
}

/**
 * A list of employees that can display various employee information and attributes.
 *
 * @example
 * ```tsx
 * <EmployeeList
 *   employees={employees}
 *   showTitles={true}
 *   showSecondaryText={true}
 * />
 * ```
 */
export function EmployeeList({
  employees,
  showTitles = false,
  showSecondaryText = false,
  showDivider = false,
  heightLimited = false,
  openProfileInNewTab = false,
  disableProfileLink = false,
  profilePicIconOverride,
  footerComponent = null,
}: EmployeeListProps): JSX.Element {
  // scrollable if resulting content is taller than 248 px
  const listContainerStyle = cx({
    [styles["height-limited"]]: heightLimited,
  });

  function EmployeeInfoWrapper({
    children,
    employee,
  }: EmployeeInfoWrapperProps): JSX.Element {
    return disableProfileLink ? (
      <div
        className={cx(
          styles["text-container"],
          styles["text-container-disabled"]
        )}
        style={{ cursor: "default" }}
      >
        {children}
      </div>
    ) : (
      <a
        className={styles["text-container"]}
        href={employee.profileUrl}
        target={openProfileInNewTab ? "_blank" : "_self"}
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <div data-testid="employee-list-container" className={listContainerStyle}>
      <ul className={styles.employees} data-testid="employee-list">
        {employees.map((employee, i) => {
          return (
            <React.Fragment key={employee.id}>
              {showDivider && i > 0 && (
                <li>
                  <Divider />
                </li>
              )}
              <li
                className={styles["list-item"]}
                data-testid="employee-list-item"
              >
                <div className={styles["employee-container"]}>
                  <ProfilePic
                    user={employee}
                    size="medium"
                    className={styles["list-profile-pic"]}
                    icon={profilePicIconOverride}
                  />
                  <EmployeeInfoWrapper employee={employee}>
                    <Text size="paragraph">
                      {/* TODO KAZ-587 Clarify whether employee has name vs fullName
                       *  and adjust below line
                       */}
                      {employee.fullName || employee.name}
                    </Text>
                    {showTitles && (
                      <Text size="small" data-testid="employee-job-title">
                        {employee.jobTitle}
                      </Text>
                    )}
                    {showSecondaryText && employee.featuredGroup && (
                      <Text
                        size="paragraphSmall"
                        data-testid="employee-secondary-text"
                        className={styles["secondary-text"]}
                      >
                        {employee.featuredGroup}
                      </Text>
                    )}
                  </EmployeeInfoWrapper>
                </div>
              </li>
            </React.Fragment>
          );
        })}
        {footerComponent}
      </ul>
    </div>
  );
}
